import mediaInfoFactory from "mediainfo.js";
import type { ReadChunkFunc, MediaInfo, MediaInfoFactoryOptions, FormatType, MediaInfoResult } from "mediainfo.js";
import { useState } from "react";
import useAsyncCallback from "./useAsyncCallback";

/** Utility function to read a file into a `Uin8Array` */
function makeReadChunk(file: File): ReadChunkFunc {
    return async (chunkSize: number, offset: number) =>
        new Uint8Array(await file.slice(offset, offset + chunkSize).arrayBuffer());
}

/** Manage an instance of `MediaInfo` for analyzing media files */
export default function useMediaInfo(format: FormatType = "object") {
    const [mediaInfo, setMediaInfo] = useState<MediaInfo<FormatType>>(undefined);

    useAsyncCallback(async () => {
        await mediaInfoFactory({
            locateFile: (filename) => filename,
            format,
        } as MediaInfoFactoryOptions<typeof format>)
            .then(setMediaInfo)
            .catch((error: unknown) => {
                console.error("Error loading media info:", error);
            });

        return () => {
            mediaInfo?.close();
        };
    }, []);

    function analyzeFile(file: File): Promise<MediaInfoResult | string | undefined> {
        if (!mediaInfo) {
            return;
        }

        return mediaInfo.analyzeData(file.size, makeReadChunk(file));
    }

    return {
        mediaInfo,
        /** Directly extract track data from a file using `MediaInfo`  */
        analyzeFile,
    };
}
